import {
  type SearchSelectOption,
  type SearchSelectPage,
} from '@/components/ui';
import { apiClient } from '@/lib/apiClient';
import type { ProductDetail, ProductSummary } from '@/lib/api-admin/types';

const PAGE_SIZE = 20;

const summaryToOption = (p: ProductSummary): SearchSelectOption => ({
  value: p.productId,
  label: p.name,
  hint:
    [p.productType, p.brandName ?? undefined, p.artistName ?? undefined]
      .filter(Boolean)
      .join(' · ') || undefined,
  thumbnailUrl: p.thumbnailUrl || null,
});

const detailToOption = (p: ProductDetail): SearchSelectOption => {
  const label = p.localizedNames?.['ko-KR'] ?? p.name ?? '이름 없음';
  // 라벨 정책: raw productId hint 노출 금지. brandName / productType 로 보조 식별자.
  const hint =
    [p.productType, p.brandName ?? undefined].filter(Boolean).join(' · ') ||
    undefined;
  return {
    value: p.productId,
    label,
    hint,
    thumbnailUrl: p.detailImages?.[0] ?? null,
  };
};

interface ProductListPayload {
  items?: ProductSummary[];
  content?: ProductSummary[];
  totalElements?: number;
  number?: number;
  size?: number;
}

export const fetchProductOptions = async (
  keyword: string,
  page: number,
): Promise<SearchSelectPage> => {
  const trimmed = keyword.trim();
  const query: Record<string, unknown> = { size: PAGE_SIZE, page };
  if (trimmed) query.keyword = trimmed;

  const [data, meta] = await apiClient.request('get', '/admin/v1/products', {
    params: { query },
  });
  const payload = data as ProductListPayload;
  const rawItems = payload.items ?? payload.content ?? [];
  const items = rawItems.map(summaryToOption);

  const totalItems = meta?.pagination?.totalItems ?? payload.totalElements;
  const hasMore =
    totalItems != null
      ? (page + 1) * PAGE_SIZE < totalItems
      : items.length === PAGE_SIZE;

  return totalItems != null
    ? { items, hasMore, total: totalItems }
    : { items, hasMore };
};

export const resolveProductOption = async (
  productId: string,
): Promise<SearchSelectOption | null> => {
  try {
    const [data] = await apiClient.request('get', '/admin/v1/products/{productId}', {
      params: { path: { productId } },
    });
    return detailToOption(data as ProductDetail);
  } catch {
    return null;
  }
};
